import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

import { BRAND, TOKENS } from '../theme';
import { AuditRow } from './ManagerModificationsList';
import useTicketPhotoViewer from './useTicketPhotoViewer';

// Filter chips, in display order. `types` are TransactionAuditLog entityTypes.
const FILTERS = [
  { id: 'all', label: 'All' },
  {
    id: 'transaction',
    label: 'Transactions',
    types: ['TRANSACTION_BANK', 'TRANSACTION_EXPENSE', 'CASH_LEFT'],
  },
  { id: 'transfer', label: 'Transfers', types: ['TRANSFER'] },
  { id: 'ticket', label: 'Ticket Out', types: ['TICKET_OUT'] },
  { id: 'bonus', label: 'Bonus', types: ['BONUS'] },
  { id: 'points', label: 'Points Match', types: ['POINT'] },
  { id: 'raffle', label: 'Raffle', types: ['RAFFLE'] },
];

const matches = (filter, entry) =>
  !filter.types || filter.types.includes(entry?.entityType);

// Full audit trail for one shift: every edit / delete / add (by the employee
// or a manager), approve/flag decisions, and employee-to-employee transfers,
// newest first. Shared by Employee Session → Audit Log and the Transaction
// screen's own-shift history.
export default function AuditLogList({
  auditLog = [],
  customers = [],
  employees = [],
  manageBonusList = [],
  emptyText = 'No changes have been recorded for this session.',
  hideFilters = false,
}) {
  const [filterId, setFilterId] = React.useState('all');
  const photoViewer = useTicketPhotoViewer();
  const lookups = { customers, employees, manageBonusList };

  const sorted = React.useMemo(
    () =>
      [...(auditLog || [])].sort(
        (a, b) =>
          new Date(b.changedAt || 0).getTime() -
          new Date(a.changedAt || 0).getTime()
      ),
    [auditLog]
  );

  // Only offer chips that have something behind them ("All" always shows).
  const chips = FILTERS.map((f) => ({
    ...f,
    count: sorted.filter((e) => matches(f, e)).length,
  })).filter((f) => f.id === 'all' || f.count > 0);

  const active = chips.find((f) => f.id === filterId) || chips[0];
  const visible = sorted.filter((e) => matches(active, e));

  if (!sorted.length) {
    return (
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          py: 5,
          px: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          textAlign: 'center',
        }}
      >
        <HistoryIcon sx={{ fontSize: 32, color: TOKENS.slate400 }} />
        <Typography sx={{ fontSize: 14, color: TOKENS.slate500 }}>
          {emptyText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: hideFilters ? 'none' : 'flex',
          gap: 0.75,
          flexWrap: 'wrap',
          mb: 1.5,
        }}
      >
        {chips.map((f) => {
          const selected = f.id === active.id;
          return (
            <Chip
              key={f.id}
              label={`${f.label} (${f.count})`}
              onClick={() => setFilterId(f.id)}
              sx={{
                fontWeight: 700,
                fontSize: 14,
                height: 32,
                background: selected ? BRAND.navy : '#fff',
                color: selected ? '#fff' : TOKENS.slate700,
                border: `1px solid ${selected ? BRAND.navy : TOKENS.slate200}`,
                '&:hover': {
                  background: selected ? BRAND.navy : TOKENS.slate50,
                },
              }}
            />
          );
        })}
      </Box>
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {visible.map((entry, i) => (
          <AuditRow
            key={`${entry.auditId || i}-${entry.sessionId || ''}`}
            entry={entry}
            lookups={lookups}
            isLast={i === visible.length - 1}
            onViewPhoto={photoViewer.view}
            showSource
            showDeletedValues
            large
          />
        ))}
      </Box>
      {photoViewer.node}
    </Box>
  );
}
